import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from "react-router";
import { queryClient, trpc } from "../../app/trpc.js";
import { BellIcon, CalendarIcon, ChatIcon, ClientsIcon, MenuIcon, Spinner, useWide } from "../../ui/index.js";
import { headerBackTo } from "./BackLink.js";
import { useCabinet } from "./context.js";
import { useClaimDevicePush } from "./device-push.js";
import { CabinetShell, type ShellNavItem } from "./Shell.js";

/**
 * Кабинет владельца и мастера: навигация (календарь, диалоги, клиенты, уведомления, настройки) в `CabinetShell`.
 * Счётчики обновляются раз в 30 секунд.
 */
export function CabinetLayout() {
  const { t } = useTranslation();
  const { salonId = "" } = useParams();
  const { pathname, search } = useLocation();
  const cab = useCabinet();
  const wide = useWide();
  const salons = useQuery(trpc.auth.mySalons.queryOptions());
  const enabled = Boolean(salons.data?.some((s) => s.id === salonId));
  const unread = useQuery(trpc.cabinet.notifications.unreadCount.queryOptions({ salonId }, { enabled, refetchInterval: 30_000 }));
  const waiting = useQuery(
    trpc.inbox.list.queryOptions({ salonId, state: "escalated", channel: "instagram", limit: 200 }, { enabled, refetchInterval: 30_000 }),
  );

  useClaimDevicePush();
  // Открыли из push: `?n=<id>` велит отметить уведомление прочитанным и убрать параметр из адреса.
  const [params, setParams] = useSearchParams();
  const fromPush = params.get("n");
  const markRead = useMutation(
    trpc.cabinet.notifications.markRead.mutationOptions({
      onSettled: () => queryClient.invalidateQueries(trpc.cabinet.notifications.pathFilter()),
    }),
  );
  const { mutate: mark } = markRead;
  useEffect(() => {
    if (!fromPush || !enabled) return;
    mark({ notificationId: fromPush });
    setParams(
      (p) => {
        p.delete("n");
        return p;
      },
      { replace: true },
    );
  }, [fromPush, enabled, mark, setParams]);

  if (salons.isPending) return <Spinner />;
  const salon = salons.data?.find((s) => s.id === salonId);
  if (!salon) return <Navigate to="/" replace />;

  const base = `/s/${salonId}`;
  const unreadCount = unread.data?.count ?? 0;
  const backTo = headerBackTo(pathname, base, { isOwner: cab.isOwner, ownerKnown: !cab.loading, billingIsTab: false, wide, search });
  const items: ShellNavItem[] = [
    { to: `${base}/calendar`, icon: <CalendarIcon />, label: t("nav.calendar") },
    { to: `${base}/conversations`, icon: <ChatIcon />, label: t("nav.conversations"), badge: waiting.data?.length ?? 0 },
    { to: `${base}/clients`, icon: <ClientsIcon />, label: t("nav.clients") },
    { to: `${base}/notifications`, icon: <BellIcon />, label: t("nav.notifications"), badge: unreadCount, badgeTone: "red" },
    { to: `${base}/settings`, icon: <MenuIcon />, label: t("nav.more"), wideLabel: t("nav.settings") },
  ];
  return (
    <CabinetShell salonName={salon.name} items={items} backTo={backTo}>
      <Outlet context={{ salon }} />
    </CabinetShell>
  );
}
